import type { TagOption } from 'components/shared/Tags/model';
import { CommodityUnderlyingResultTable } from 'components/shared/underlyings/selector/result/Commodity';
import { InputTextContext } from 'components/shared/underlyings/selector/result/Common';
import { CreditUnderlyingResultTable } from 'components/shared/underlyings/selector/result/Credit';
import { EquityUnderlyingResultTable } from 'components/shared/underlyings/selector/result/Equity';
import { ForexUnderlyingResultTable } from 'components/shared/underlyings/selector/result/Forex';
import { NoOptionsResultPanel } from 'components/shared/underlyings/selector/result/no-option-message';
import { RatesUnderlyingResultTable } from 'components/shared/underlyings/selector/result/Rate';
import type { Underlying } from 'models/underlying';
import { components, GroupBase, type MenuListProps } from 'react-select';
import {
  isCommodityUnderlying,
  isCreditUnderlying,
  isForexUnderlying,
  isRateUnderlying,
} from 'utils/underlying/typeGuards';

export function MenuList(props: MenuListProps<TagOption<Underlying>, boolean>) {
  const UnderlyingResultComponent = getUnderlyingResultComponent(props.options);

  return (
    <components.MenuList {...props} className="bg-lvl1 border border-light">
      <InputTextContext.Provider value={props.selectProps.inputValue ?? ''}>
        <UnderlyingResultComponent options={props.options}>{props.children}</UnderlyingResultComponent>
      </InputTextContext.Provider>
    </components.MenuList>
  );
}

export function getUnderlyingResultComponent(
  options: ReadonlyArray<TagOption<Underlying> | GroupBase<TagOption<Underlying>>>,
) {
  const hasNoOptions = options.length === 0;
  if (hasNoOptions) {
    return NoOptionsResultPanel;
  }

  const hasCreditOption = options.some(
    option => 'rawValue' in option && !!option.rawValue && isCreditUnderlying(option.rawValue),
  );
  if (hasCreditOption) {
    return CreditUnderlyingResultTable;
  }

  const hasRatesOption = options.some(
    option => 'rawValue' in option && !!option.rawValue && isRateUnderlying(option.rawValue),
  );
  if (hasRatesOption) {
    return RatesUnderlyingResultTable;
  }

  const hasForexOption = options.some(
    option => 'rawValue' in option && !!option.rawValue && isForexUnderlying(option.rawValue),
  );
  if (hasForexOption) {
    return ForexUnderlyingResultTable;
  }

  const hasCommodityOption = options.some(
    option => 'rawValue' in option && !!option.rawValue && isCommodityUnderlying(option.rawValue),
  );
  if (hasCommodityOption) {
    return CommodityUnderlyingResultTable;
  }

  return EquityUnderlyingResultTable;
}
